import React, { useEffect, useMemo, useState } from "react";
import * as THREE from "three";

/**
 * Prosty loader tekstury z URL (opcjonalny)
 * - używamy tylko do przypadków typu "drewno" kiedy toolingColor.texture jest URL-em
 */
function useOptionalTexture(url) {
  const [tex, setTex] = useState(null);

  useEffect(() => {
    if (!url) {
      setTex(null);
      return;
    }

    let alive = true;
    const loader = new THREE.TextureLoader();

    loader.load(
      url,
      (t) => {
        if (!alive) {
          t.dispose?.();
          return;
        }

        // sRGB (dla zwykłych obrazków)
        try {
          t.colorSpace = THREE.SRGBColorSpace;
        } catch (e) {}

        t.wrapS = THREE.RepeatWrapping;
        t.wrapT = THREE.RepeatWrapping;
        t.repeat.set(1, 1);
        t.needsUpdate = true;

        setTex(t);
      },
      undefined,
      () => {
        if (alive) setTex(null);
      }
    );

    return () => {
      alive = false;
    };
  }, [url]);

  // sprzątanie URL-owych tekstur (tych z TextureLoader)
  useEffect(() => {
    return () => {
      tex?.dispose?.();
    };
  }, [tex]);

  return tex;
}

/**
 * Beam = belka jako box ustawiony wzdłuż odcinka A->B.
 * - oś boxa jest wzdłuż Y, więc obracamy Y -> kierunek odcinka
 * - overlap wydłuża belkę symetrycznie na obu końcach (eliminuje szpary)
 */
function Beam({ a, b, thickness = 0.05, overlap = 0.02, material }) {
  const { position, quaternion, length } = useMemo(() => {
    const dir = new THREE.Vector3().subVectors(b, a);
    const baseLen = dir.length();
    const safeLen = Math.max(baseLen, 1e-6);

    const mid = new THREE.Vector3().addVectors(a, b).multiplyScalar(0.5);

    const q = new THREE.Quaternion();
    q.setFromUnitVectors(new THREE.Vector3(0, 1, 0), dir.clone().normalize());

    const extendedLen = safeLen + 2 * overlap;

    return { position: mid, quaternion: q, length: extendedLen };
  }, [a, b, overlap]);

  return (
    <mesh position={position} quaternion={quaternion} material={material} castShadow receiveShadow>
      <boxGeometry args={[thickness, length, thickness]} />
    </mesh>
  );
}

/**
 * Tooling - renderuje "obróbki" jako belki:
 * - roofEdges: obrys dachu (po okapie)
 * - corners: pionowe słupy przy narożnikach ścian
 *
 * + AUTO ODPŁYW (Twoja logika)
 */
export default function Tooling({
                                  toolingData,
                                  textures,
                                  toolingColor,

                                  roofThickness = 0.06,
                                  cornerThickness = 0.08,

                                  roofOverlap = 0.03,
                                  cornerOverlap = 0.01,

                                  cornerTopInset = 0.02,

                                  enableDrainageCut = true,
                                  flatSideEps = 0.03,
                                  lowestEps = 0.03,

                                  disableRoofSides = null,
                                }) {
  if (!toolingData) return null;

  // 1) jeśli toolingColor.texture jest URL-em (np drewno) -> ładujemy normalnie
  const toolingTexUrl = useOptionalTexture(textures.metal_default?.texture);

  // 2) jeśli nie ma URL-a -> default KTX2 z loadera
  const toolingTexKtx2 = textures?.tooling_color?.texture ?? null;

  // finalna mapa: URL ma priorytet, inaczej KTX2
  const toolingMap = toolingTexUrl || toolingTexKtx2;

  // opcjonalnie: dopnij wrap/repeat na mapie (bezpieczne jeśli loader daje klony per slot)
  useEffect(() => {
    if (!toolingMap) return;
    toolingMap.wrapS = THREE.RepeatWrapping;
    toolingMap.wrapT = THREE.RepeatWrapping;
    toolingMap.repeat?.set?.(1, 1);
    toolingMap.needsUpdate = true;
  }, [toolingMap]);

  const toolingMaterial = useMemo(() => {
    // ✅ DACH: kolor z roof_tooling_color
    const roofTooling = textures?.roof_tooling_color;
    const roofHex = roofTooling?.hex || roofTooling?.hexa || "#d9d9d9";

    const isWood = toolingColor?.type === "wooden";

    // zachowujemy dotychczasową logikę metalness/roughness (bez wallColor)
    const metalness = toolingColor?.metalnessValue ?? (isWood ? 0 : 0.7);
    const roughness = toolingColor?.roughnessValue ?? (isWood ? 0.9 : 0.85);

    const mat = new THREE.MeshStandardMaterial({
      color: new THREE.Color(roofHex),
      metalness,
      roughness,
      side: THREE.FrontSide,
    });

    if (toolingMap) {
      mat.map = toolingMap;
    }

    mat.needsUpdate = true;
    return mat;
  }, [
    textures?.roof_tooling_color,
    toolingColor?.type,
    toolingColor?.metalnessValue,
    toolingColor?.roughnessValue,
    toolingMap,
  ]);

  const cornerMaterial = useMemo(() => {
    // ✅ PIONOWE BELKI: kolor z tooling_color
    const tool = textures?.tooling_color;
    const toolHex = tool?.hex || tool?.hexa || "#d9d9d9";

    const isWood = toolingColor?.type === "wooden";

    const metalness = toolingColor?.metalnessValue ?? (isWood ? 0 : 0.7);
    const roughness = toolingColor?.roughnessValue ?? (isWood ? 0.9 : 0.85);

    const mat = new THREE.MeshStandardMaterial({
      color: new THREE.Color(toolHex),
      metalness,
      roughness,
      side: THREE.FrontSide,
    });

    if (toolingMap) {
      mat.map = toolingMap;
    }

    mat.needsUpdate = true;
    return mat;
  }, [
    textures?.tooling_color,
    toolingColor?.type,
    toolingColor?.metalnessValue,
    toolingColor?.roughnessValue,
    toolingMap,
  ]);

  useEffect(() => {
    return () => {
      toolingMaterial?.dispose?.();
    };
  }, [toolingMaterial]);

  useEffect(() => {
    return () => {
      cornerMaterial?.dispose?.();
    };
  }, [cornerMaterial]);

  const roofEdgesFinal = useMemo(() => {
    const edges = toolingData?.roofEdges || [];
    if (!edges.length) return edges;

    const manualDisabled =
      Array.isArray(disableRoofSides) && disableRoofSides.length
        ? new Set(disableRoofSides)
        : null;

    if (!enableDrainageCut && !manualDisabled) return edges;

    const pts = [];
    for (const e of edges) {
      if (e?.a) pts.push(e.a);
      if (e?.b) pts.push(e.b);
    }
    if (pts.length < 2) return edges;

    const box = new THREE.Box3().setFromPoints(pts);

    const minX = box.min.x;
    const maxX = box.max.x;
    const minZ = box.min.z;
    const maxZ = box.max.z;

    const xSpan = Math.max(1e-6, maxX - minX);
    const zSpan = Math.max(1e-6, maxZ - minZ);

    const sideSnap = Math.max(0.03, Math.min(xSpan, zSpan) * 0.06);

    const segMeta = edges.map((seg) => {
      const ax = seg.a.x,
        bx = seg.b.x;
      const az = seg.a.z,
        bz = seg.b.z;

      const scoreMinX = Math.max(Math.abs(ax - minX), Math.abs(bx - minX));
      const scoreMaxX = Math.max(Math.abs(ax - maxX), Math.abs(bx - maxX));
      const scoreMinZ = Math.max(Math.abs(az - minZ), Math.abs(bz - minZ));
      const scoreMaxZ = Math.max(Math.abs(az - maxZ), Math.abs(bz - maxZ));

      let side = "minX";
      let best = scoreMinX;

      if (scoreMaxX < best) {
        best = scoreMaxX;
        side = "maxX";
      }
      if (scoreMinZ < best) {
        best = scoreMinZ;
        side = "minZ";
      }
      if (scoreMaxZ < best) {
        best = scoreMaxZ;
        side = "maxZ";
      }

      const nearAny = best <= sideSnap;

      const y1 = seg.a.y;
      const y2 = seg.b.y;
      const avgY = (y1 + y2) * 0.5;

      return { seg, side, nearAny, y1, y2, avgY };
    });

    const sides = { minX: [], maxX: [], minZ: [], maxZ: [] };
    for (const m of segMeta) {
      if (m.nearAny) sides[m.side].push(m);
    }

    const sideStats = Object.entries(sides).map(([side, arr]) => {
      if (!arr.length) {
        return { side, count: 0, meanY: Infinity, yRange: Infinity, isFlat: false };
      }

      let sum = 0;
      let minY = Infinity;
      let maxY = -Infinity;

      for (const m of arr) {
        sum += m.avgY;
        minY = Math.min(minY, m.y1, m.y2);
        maxY = Math.max(maxY, m.y1, m.y2);
      }

      const meanY = sum / arr.length;
      const yRange = maxY - minY;
      const isFlat = yRange <= flatSideEps;

      return { side, count: arr.length, meanY, yRange, isFlat };
    });

    const statsBySide = sideStats.reduce((acc, s) => {
      acc[s.side] = s;
      return acc;
    }, {});

    if (!enableDrainageCut) {
      return segMeta
        .filter((m) => !(m.nearAny && manualDisabled && manualDisabled.has(m.side)))
        .map((m) => m.seg);
    }

    const flatSides = sideStats.filter((s) => s.count > 0 && s.isFlat);

    if (!flatSides.length) {
      if (!manualDisabled) return edges;
      return segMeta
        .filter((m) => !(m.nearAny && manualDisabled.has(m.side)))
        .map((m) => m.seg);
    }

    let minMeanY = Infinity;
    for (const s of flatSides) minMeanY = Math.min(minMeanY, s.meanY);

    const toRemove = new Set(
      flatSides.filter((s) => s.meanY <= minMeanY + lowestEps).map((s) => s.side)
    );

    const opposite = { minX: "maxX", maxX: "minX", minZ: "maxZ", maxZ: "minZ" };
    for (const side of Array.from(toRemove)) {
      const opp = opposite[side];
      const a = statsBySide[side];
      const b = statsBySide[opp];
      if (
        a?.count > 0 &&
        b?.count > 0 &&
        a.isFlat &&
        b.isFlat &&
        Math.abs(a.meanY - b.meanY) <= lowestEps
      ) {
        toRemove.add(opp);
      }
    }

    if (manualDisabled) {
      for (const side of manualDisabled) toRemove.add(side);
    }

    return segMeta
      .filter((m) => !(m.nearAny && toRemove.has(m.side)))
      .map((m) => m.seg);
  }, [
    toolingData?.roofEdges,
    enableDrainageCut,
    flatSideEps,
    lowestEps,
    disableRoofSides,
  ]);

  const cornerSegmentsFinal = useMemo(() => {
    const corners = toolingData?.corners || [];
    if (!corners.length) return corners;

    const inset = Math.max(0, Number(cornerTopInset) || 0);
    if (inset <= 0) return corners;

    return corners.map((seg) => {
      const a = seg.a;
      const b = seg.b;

      const dir = new THREE.Vector3().subVectors(b, a);
      const len = dir.length();

      if (len < 1e-6 || inset >= len) return seg;

      dir.normalize();
      const newB = b.clone().addScaledVector(dir, -inset);

      return { ...seg, b: newB };
    });
  }, [toolingData?.corners, cornerTopInset]);

  return (
    <group>
      {roofEdgesFinal.map((seg, i) => (
        <Beam
          key={`roof-${i}`}
          a={seg.a}
          b={seg.b}
          thickness={roofThickness}
          overlap={roofOverlap}
          material={toolingMaterial} // ✅ roof_tooling_color
        />
      ))}

      {cornerSegmentsFinal.map((seg, i) => (
        <Beam
          key={`corner-${i}`}
          a={seg.a}
          b={seg.b}
          thickness={cornerThickness}
          overlap={cornerOverlap}
          material={cornerMaterial} // ✅ tooling_color
        />
      ))}
    </group>
  );
}
